import axios from 'axios';
import { tokenStorage } from '../utils/auth';
import { getBackendUrl } from '../utils/https';

export type AIProviderType = 'claude' | 'gemini' | 'deepseek' | 'qwen' | 'kimi' | 'openai' | 'perplexity';

/**
 * The jobs a model can be assigned to. Every AI feature belongs to one, so
 * these five choices cover every use of AI in the system.
 * `businessCard` keeps its old name because settings already store it.
 */
export type AIUseCase = 'businessCard' | 'writing' | 'search' | 'research' | 'analysis';

/** Chosen model per provider and use case; missing entries use the provider default */
export type AIModelSelections = Partial<Record<AIProviderType, Partial<Record<AIUseCase, string>>>>;

/** USD per 1M tokens */
export interface AIModelPricing {
  input: number | null;
  output: number | null;
  cacheRead: number | null;
  cacheWrite: number | null;
  tiered: boolean;
}

export interface AIModelInfo {
  id: string;
  name: string;
  description: string | null;
  releaseDate: string | null;
  knowledgeCutoff: string | null;
  status: 'active' | 'deprecated' | 'beta' | 'preview';
  category: 'chat' | 'other';
  inputModalities: string[];
  outputModalities: string[];
  capabilities: {
    imageInput: boolean | null;
    pdfInput: boolean | null;
    audioInput: boolean | null;
    videoInput: boolean | null;
    reasoning: boolean | null;
    toolCalling: boolean | null;
    structuredOutput: boolean | null;
    temperature: boolean | null;
  };
  reasoning: {
    canDisable: boolean;
    alwaysOn: boolean;
    efforts: string[];
    budget: boolean;
    budgetMin: number | null;
  } | null;
  contextWindow: number | null;
  maxOutputTokens: number | null;
  pricing: AIModelPricing | null;
  availableToKey: boolean;
  hasCatalogData: boolean;
}

export interface AIProviderModelsResult {
  provider: AIProviderType;
  models: AIModelInfo[];
  fetchedAt: string;
  liveList: {
    status: 'ok' | 'error' | 'no_key';
    message?: string;
    count: number;
    region?: string;
  };
  catalog: {
    status: 'ok' | 'stale' | 'unavailable';
    source: string;
    sourceUrl: string;
    fetchedAt: string | null;
    message?: string;
  };
  defaults: Partial<Record<AIUseCase, string>>;
  notes: string[];
  pricingUrl: string;
}

export interface AISettings {
  provider: AIProviderType;
  apiKey?: string;
  config?: Record<string, any>;
  writingProvider?: AIProviderType;
  researchProvider?: AIProviderType;
  searchProvider?: AIProviderType;
  analysisProvider?: AIProviderType;
  apiKeyMasked?: boolean;
  apiKeys?: Record<string, string>;
  apiKeysMasked?: Record<string, boolean>;
  models?: AIModelSelections;
  defaultModels?: Record<string, Partial<Record<AIUseCase, string>>>;
  modelDetails?: Record<string, Partial<Record<AIUseCase, AIModelInfo | null>>>;
}

export interface AIConnectionCheck {
  id: 'text' | 'image' | 'webSearch';
  label: string;
  success: boolean;
  message: string;
  latencyMs: number;
}

export interface TestConnectionResult {
  success: boolean;
  message: string;
  model?: string;
  checks?: AIConnectionCheck[];
}

class SettingsService {
  private baseURL = `${getBackendUrl()}/api`;

  private getAuthHeaders() {
    const token = tokenStorage.get();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  async getAISettings(): Promise<AISettings> {
    const response = await axios.get(`${this.baseURL}/settings/ai-provider`, {
      headers: this.getAuthHeaders(),
    });
    return response.data as any;
  }

  async updateAISettings(settings: Partial<AISettings>): Promise<AISettings> {
    const response = await axios.post(
      `${this.baseURL}/settings/ai-provider`,
      settings,
      {
        headers: {
          ...this.getAuthHeaders(),
          'Content-Type': 'application/json',
        },
      },
    );
    return response.data as any;
  }

  /**
   * Tests a provider with a model for a use case. `apiKey` lets an admin test a
   * key they have typed but not saved yet.
   */
  async testAIConnection(
    provider: string,
    options: { model?: string; useCase?: AIUseCase; apiKey?: string } = {},
  ): Promise<TestConnectionResult> {
    const response = await axios.post(
      `${this.baseURL}/settings/ai-provider/test`,
      { provider, ...options },
      {
        headers: this.getAuthHeaders(),
      },
    );
    return response.data as any;
  }

  /** Models available to the provider's key, with pricing and capabilities. */
  async getProviderModels(
    provider: string,
    options: { apiKey?: string; refresh?: boolean } = {},
  ): Promise<AIProviderModelsResult> {
    const response = await axios.post(
      `${this.baseURL}/settings/ai-provider/models`,
      { provider, ...options },
      {
        headers: this.getAuthHeaders(),
      },
    );
    return response.data as any;
  }
}

export const settingsService = new SettingsService();
